import React from "react";

/**
 * Minimalist SVG sketches for each screen type.
 * Swiss Brutalist style: thin lines, sharp edges, accent color for tracks.
 */

const TRACK_COLOR = "#005bea";
const FRAME_COLOR = "#09090b";
const MUTED = "#a1a1aa";

const Label = ({ x, y, children, anchor = "middle", size = 9 }) => (
  <text
    x={x}
    y={y}
    textAnchor={anchor}
    fontFamily="JetBrains Mono, monospace"
    fontSize={size}
    fill={MUTED}
  >
    {children}
  </text>
);

const Base = ({ children, title }) => (
  <svg
    viewBox="0 0 220 160"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-auto"
    preserveAspectRatio="xMidYMid meet"
  >
    {children}
    <text
      x={110}
      y={152}
      textAnchor="middle"
      fontFamily="Cabinet Grotesk, sans-serif"
      fontWeight="700"
      fontSize={9}
      fill={FRAME_COLOR}
      letterSpacing={1.5}
    >
      {title.toUpperCase()}
    </text>
  </svg>
);

// Shared rects
const Frame = ({ x, y, w, h }) => (
  <rect
    x={x}
    y={y}
    width={w}
    height={h}
    fill="none"
    stroke={FRAME_COLOR}
    strokeWidth={1.5}
  />
);

const PatioNoTrack = () => (
  <Base title="Patio Door · no track">
    <Frame x={60} y={12} w={100} h={120} />
    {/* Midrail */}
    <line x1={60} y1={72} x2={160} y2={72} stroke={FRAME_COLOR} strokeWidth={1} />
    <Label x={110} y={78}>MID</Label>
    {/* Dimension arrows */}
    <Label x={110} y={145}>width</Label>
    <Label x={50} y={74} anchor="end">height</Label>
  </Base>
);

const Patio15mm = () => (
  <Base title="Patio · 15mm side track">
    {/* Side tracks */}
    <rect x={52} y={12} width={8} height={120} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={160} y={12} width={8} height={120} fill={TRACK_COLOR} opacity={0.85} />
    <Frame x={60} y={12} w={100} h={120} />
    <line x1={60} y1={72} x2={160} y2={72} stroke={FRAME_COLOR} strokeWidth={1} />
    <Label x={46} y={74} anchor="end">15</Label>
    <Label x={173} y={74} anchor="start">15</Label>
    <Label x={110} y={145}>width</Label>
  </Base>
);

const Patio15Bottom = () => (
  <Base title="Patio · 1x 15mm bottom track">
    {/* Door rectangle (slightly trapezoidal hand-drawn feel) */}
    <Frame x={70} y={12} w={90} h={108} />
    {/* Mid rail */}
    <line x1={70} y1={66} x2={160} y2={66} stroke={FRAME_COLOR} strokeWidth={1} />
    {/* Bottom track — a separate line below the door, slightly wider */}
    <line
      x1={56}
      y1={128}
      x2={174}
      y2={128}
      stroke={TRACK_COLOR}
      strokeWidth={2}
    />
    <Label x={56} y={138} anchor="start" size={8}>15</Label>
    {/* Arrow + label */}
    <line x1={140} y1={128} x2={155} y2={120} stroke={MUTED} strokeWidth={0.7} />
    <polygon points="140,128 144,124 144,130" fill={MUTED} />
    <Label x={158} y={119} anchor="start" size={8}>15mm bottom track</Label>
  </Base>
);

const Patio20mm = () => (
  <Base title="Patio · 20mm top+bot tracks">
    {/* Door rectangle (slightly trapezoidal hand-drawn look) */}
    <polygon
      points="78,16 152,16 158,128 72,128"
      fill="none"
      stroke={FRAME_COLOR}
      strokeWidth={1.5}
    />
    {/* Mid rail (also slightly trapezoidal) */}
    <line x1={75} y1={70} x2={155} y2={70} stroke={FRAME_COLOR} strokeWidth={1} />

    {/* Top track — separate line above the door, extending past */}
    <line x1={60} y1={10} x2={170} y2={10} stroke={TRACK_COLOR} strokeWidth={2} />
    {/* Top arrow + label */}
    <line x1={148} y1={10} x2={170} y2={28} stroke={MUTED} strokeWidth={0.7} />
    <polygon points="148,10 152,14 145,15" fill={MUTED} />
    <Label x={172} y={28} anchor="start" size={7}>20mm Top track</Label>

    {/* Bottom track — separate line below the door, extending past */}
    <line x1={56} y1={134} x2={174} y2={134} stroke={TRACK_COLOR} strokeWidth={2} />
    <Label x={56} y={144} anchor="start" size={7}>20 mm</Label>
    {/* Bottom arrow + label */}
    <line x1={148} y1={134} x2={170} y2={120} stroke={MUTED} strokeWidth={0.7} />
    <polygon points="148,134 144,131 145,138" fill={MUTED} />
    <Label x={172} y={119} anchor="start" size={7}>20 mm Bottom track</Label>
  </Base>
);

const DoublePatio = () => (
  <Base title="Double Patio Door">
    <rect x={30} y={4} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={30} y={132} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={22} y={12} width={8} height={120} fill={TRACK_COLOR} opacity={0.55} />
    <rect x={190} y={12} width={8} height={120} fill={TRACK_COLOR} opacity={0.55} />
    <Frame x={30} y={12} w={160} h={120} />
    {/* Divider */}
    <line x1={110} y1={12} x2={110} y2={132} stroke={FRAME_COLOR} strokeWidth={1.5} />
    <line x1={30} y1={72} x2={110} y2={72} stroke={FRAME_COLOR} strokeWidth={1} />
    <line x1={110} y1={72} x2={190} y2={72} stroke={FRAME_COLOR} strokeWidth={1} />
    <Label x={70} y={80} size={8}>MID</Label>
    <Label x={150} y={80} size={8}>MID</Label>
    <Label x={110} y={148} size={8}>width / 2 + width / 2</Label>
  </Base>
);

const SlidingWindowPlain = () => (
  <Base title="Sliding Window · plain">
    <Frame x={30} y={40} w={160} h={80} />
    {/* Arrow indicating slide */}
    <line x1={50} y1={80} x2={170} y2={80} stroke={MUTED} strokeWidth={1} strokeDasharray="3 3" />
    <polygon points="170,80 163,76 163,84" fill={MUTED} />
    <polygon points="50,80 57,76 57,84" fill={MUTED} />
    <Label x={110} y={33} size={8}>slide</Label>
  </Base>
);

const SlidingWindow15Side = () => (
  <Base title="Sliding Window · 15mm sides">
    <rect x={22} y={40} width={8} height={80} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={190} y={40} width={8} height={80} fill={TRACK_COLOR} opacity={0.85} />
    <Frame x={30} y={40} w={160} h={80} />
    <Label x={16} y={84} anchor="end">15</Label>
    <Label x={204} y={84} anchor="start">15</Label>
    <line x1={50} y1={80} x2={170} y2={80} stroke={MUTED} strokeWidth={1} strokeDasharray="3 3" />
  </Base>
);

const SlidingWindow15Bottom = () => (
  <Base title="Sliding Window · 15mm bottom">
    <rect x={30} y={120} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <Frame x={30} y={40} w={160} h={80} />
    <Label x={110} y={137} size={8}>15 bottom</Label>
    <line x1={50} y1={80} x2={170} y2={80} stroke={MUTED} strokeWidth={1} strokeDasharray="3 3" />
  </Base>
);

const SlidingWindow20TopBot = () => (
  <Base title="Sliding Window · 20 top+bot">
    <rect x={30} y={32} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={30} y={120} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={22} y={40} width={8} height={80} fill={TRACK_COLOR} opacity={0.55} />
    <rect x={190} y={40} width={8} height={80} fill={TRACK_COLOR} opacity={0.55} />
    <Frame x={30} y={40} w={160} h={80} />
    <Label x={110} y={29} size={8}>20</Label>
    <Label x={110} y={137} size={8}>20</Label>
    <Label x={16} y={84} anchor="end" size={8}>15</Label>
  </Base>
);

const Fixed = () => (
  <Base title="Fixed Screen">
    <Frame x={50} y={30} w={120} h={100} />
    {/* Hatching pattern */}
    <defs>
      <pattern id="fixedHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke={MUTED} strokeWidth="0.5" opacity="0.6" />
      </pattern>
    </defs>
    <rect x={50} y={30} width={120} height={100} fill="url(#fixedHatch)" />
  </Base>
);

const Pulldown = () => (
  <Base title="Pulldown Screen">
    {/* Roller box on top */}
    <rect
      x={50}
      y={22}
      width={120}
      height={12}
      fill={FRAME_COLOR}
      stroke={FRAME_COLOR}
    />
    <Label x={110} y={18} size={7}>ROLLER</Label>
    <Frame x={50} y={34} w={120} h={96} />
    {/* Horizontal lines to show pull-down motion */}
    {[50, 65, 80, 95, 110].map((y) => (
      <line key={y} x1={55} y1={y} x2={165} y2={y} stroke={MUTED} strokeWidth="0.5" opacity="0.4" />
    ))}
    <polygon points="108,130 112,130 110,136" fill={MUTED} />
  </Base>
);

const Pleated = () => (
  <Base title="Pleated Screen">
    <Frame x={50} y={30} w={120} h={100} />
    {/* Vertical pleat lines */}
    {Array.from({ length: 11 }).map((_, i) => {
      const x = 56 + i * 11;
      return (
        <line
          key={i}
          x1={x}
          y1={30}
          x2={x}
          y2={130}
          stroke={MUTED}
          strokeWidth="0.5"
          opacity="0.6"
        />
      );
    })}
    {/* Accent */}
    <path
      d="M50 80 L56 76 L67 84 L78 76 L89 84 L100 76 L111 84 L122 76 L133 84 L144 76 L155 84 L166 76 L170 80"
      stroke={TRACK_COLOR}
      strokeWidth="1"
      fill="none"
    />
  </Base>
);

const SideOpening = () => (
  <Base title="Side Opening Door">
    <Frame x={50} y={20} w={120} h={120} />
    {/* Hinge dots on left */}
    <circle cx={52} cy={30} r={2} fill={FRAME_COLOR} />
    <circle cx={52} cy={80} r={2} fill={FRAME_COLOR} />
    <circle cx={52} cy={130} r={2} fill={FRAME_COLOR} />
    {/* Door swing arc */}
    <path
      d="M50 20 Q110 40 170 20"
      fill="none"
      stroke={MUTED}
      strokeWidth="0.7"
      strokeDasharray="3 2"
    />
    <Label x={110} y={14} size={8}>open ←</Label>
  </Base>
);

const Patio20With1SideTrack = () => (
  <Base title="Patio · 20mm top+bot + 1× 15 side">
    <polygon
      points="78,16 152,16 158,128 72,128"
      fill="none"
      stroke={FRAME_COLOR}
      strokeWidth={1.5}
    />
    <line x1={75} y1={70} x2={155} y2={70} stroke={FRAME_COLOR} strokeWidth={1} />
    {/* Top + bottom tracks */}
    <line x1={60} y1={10} x2={170} y2={10} stroke={TRACK_COLOR} strokeWidth={2} />
    <line x1={56} y1={134} x2={174} y2={134} stroke={TRACK_COLOR} strokeWidth={2} />
    <Label x={60} y={8} anchor="start" size={7}>20 top</Label>
    <Label x={174} y={144} anchor="end" size={7}>20 bot</Label>
    {/* ONE side track (left only) */}
    <rect x={56} y={16} width={5} height={112} fill={TRACK_COLOR} opacity={0.7} />
    <Label x={52} y={80} anchor="end" size={7}>15</Label>
    {/* Right side — no track, dashed placeholder */}
    <line x1={172} y1={16} x2={172} y2={128} stroke={MUTED} strokeWidth={0.5} strokeDasharray="2 2" />
    <Label x={178} y={80} anchor="start" size={6.5}>no track</Label>
  </Base>
);

const Patio20WithSideTrack = () => (
  <Base title="Patio · 20mm top+bot + 15 side">
    <polygon
      points="78,16 152,16 158,128 72,128"
      fill="none"
      stroke={FRAME_COLOR}
      strokeWidth={1.5}
    />
    <line x1={75} y1={70} x2={155} y2={70} stroke={FRAME_COLOR} strokeWidth={1} />
    {/* Top + bottom tracks */}
    <line x1={60} y1={10} x2={170} y2={10} stroke={TRACK_COLOR} strokeWidth={2} />
    <line x1={56} y1={134} x2={174} y2={134} stroke={TRACK_COLOR} strokeWidth={2} />
    <Label x={60} y={8} anchor="start" size={7}>20 top</Label>
    <Label x={174} y={144} anchor="end" size={7}>20 bot</Label>
    {/* ONE side track (left only) */}
    <rect x={56} y={16} width={5} height={112} fill={TRACK_COLOR} opacity={0.7} />
    <Label x={52} y={80} anchor="end" size={7}>15</Label>
  </Base>
);

const DoublePatioWithSideTrack = () => (
  <Base title="Double Patio · with side track">
    <rect x={30} y={4} width={160} height={6} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={30} y={134} width={160} height={6} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={20} y={12} width={6} height={120} fill={TRACK_COLOR} opacity={0.85} />
    <Frame x={30} y={12} w={160} h={120} />
    <line x1={110} y1={12} x2={110} y2={132} stroke={FRAME_COLOR} strokeWidth={1.5} />
    <line x1={30} y1={72} x2={190} y2={72} stroke={FRAME_COLOR} strokeWidth={1} />
    <Label x={16} y={76} anchor="end" size={7}>15</Label>
    <Label x={110} y={148} size={8}>double leaf</Label>
  </Base>
);

const SlidingWindow20WithSideTrack = () => (
  <Base title="Sliding · 20 top+bot + 15 side">
    <rect x={30} y={32} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={30} y={120} width={160} height={8} fill={TRACK_COLOR} opacity={0.85} />
    <rect x={20} y={40} width={6} height={80} fill={TRACK_COLOR} opacity={0.85} />
    <Frame x={30} y={40} w={160} h={80} />
    <Label x={110} y={29} size={8}>20 top</Label>
    <Label x={110} y={137} size={8}>20 bot</Label>
    <Label x={16} y={84} anchor="end" size={7}>15</Label>
  </Base>
);

const Patio15BottomWithSideTrack = () => (
  <Base title="Patio · 15mm bottom + side track">
    {/* Side track bar (left) */}
    <rect x={50} y={12} width={6} height={108} fill={TRACK_COLOR} opacity={0.85} />
    {/* Door rectangle (slightly trapezoidal) */}
    <Frame x={60} y={12} w={100} h={108} />
    {/* Mid rail */}
    <line x1={60} y1={66} x2={160} y2={66} stroke={FRAME_COLOR} strokeWidth={1} />
    {/* Bottom track */}
    <line x1={46} y1={128} x2={174} y2={128} stroke={TRACK_COLOR} strokeWidth={2} />
    {/* Labels */}
    <line x1={54} y1={30} x2={30} y2={22} stroke={MUTED} strokeWidth={0.7} />
    <polygon points="54,30 50,28 50,34" fill={MUTED} />
    <Label x={28} y={20} anchor="end" size={7}>15 side</Label>
    <Label x={46} y={138} anchor="start" size={7}>15 bottom</Label>
  </Base>
);

const SKETCHES = {
  patio_door_no_track: PatioNoTrack,
  patio_door_15mm_track: Patio15mm,
  patio_door_15mm_bottom: Patio15Bottom,
  patio_door_with_side_track: Patio15BottomWithSideTrack,
  patio_door_20mm_track: Patio20mm,
  patio_door_20mm_with_side_track: Patio20WithSideTrack,
  double_patio_door: DoublePatio,
  double_patio_door_with_side_track: DoublePatioWithSideTrack,
  sliding_window: SlidingWindowPlain,
  sliding_window_15mm_track: SlidingWindow15Side,
  sliding_window_15mm_bottom: SlidingWindow15Bottom,
  sliding_window_20mm_track: SlidingWindow20TopBot,
  sliding_window_20mm_with_side_track: SlidingWindow20WithSideTrack,
  fixed: Fixed,
  pulldown: Pulldown,
  pulldown_door: Pulldown,
  pleated: Pleated,
  side_opening_door: SideOpening,
};

const ScreenSketch = ({ typeKey }) => {
  const Comp = SKETCHES[typeKey];
  if (!Comp) {
    return (
      <div className="w-full aspect-[220/160] border border-dashed border-zinc-200 flex items-center justify-center">
        <span className="font-mono text-[10px] text-zinc-400">no sketch</span>
      </div>
    );
  }
  return (
    <div className="w-full border border-zinc-200 bg-[#fafafa] p-2">
      <Comp />
    </div>
  );
};

export default ScreenSketch;
